import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { FiAward, FiLock, FiRefreshCw, FiTarget, FiTrendingUp, FiUsers, FiZap } from "react-icons/fi";
import { getSession } from "../utils/session";
import { API_URL } from "../services/api";
import Avatar from "./Avatar";
import "./ComunidadPanel.css";

/*
 * El muro de la comunidad.
 *
 * Nadie escribe nada aquí: los logros salen solos de los entrenos. Y no se
 * ve nada hasta que uno entra, porque mirar lo de los demás sin poner nada
 * propio no es una comunidad, es un escaparate.
 */

const auth = async () => ({ Authorization: `Bearer ${(await getSession()).token}` });

const ICONOS = {
  "primer-entreno": "🎉",
  entreno: "💪",
  record: "🏆",
  racha: "🔥",
  constancia: "🧱",
  vuelve: "👊",
};

function cuando(ts) {
  const min = Math.round((Date.now() - new Date(ts)) / 60000);
  if (min < 2) return "ahora";
  if (min < 60) return `hace ${min} min`;
  if (min < 60 * 24) return `hace ${Math.round(min / 60)} h`;
  const d = Math.round(min / 1440);
  if (d === 1) return "ayer";
  if (d < 7) return `hace ${d} días`;
  return new Date(ts).toLocaleDateString("es-ES", { day: "numeric", month: "short" });
}

const METRICA = { entrenos: "entrenos", minutos: "minutos", volumen: "kg movidos" };

/* ─── Entrar o salir ─── */

function Puerta({ onEntrar, participantes, guardando }) {
  const [apodo, setApodo] = useState("");

  return (
    <section className="cm-puerta">
      <FiUsers className="cm-puerta-icono" aria-hidden="true" />
      <h2>Entrena acompañado</h2>
      <p>
        Cuando entras, tus entrenos, tus marcas y tus rachas aparecen en un
        muro que ven los demás clientes de Titan. Y tú ves los suyos. No hay
        que escribir nada: sale solo de lo que ya haces.
      </p>
      <p className="cm-puerta-gente">
        {participantes === 0
          ? "Serías el primero en entrar."
          : participantes === 1
          ? "Hay 1 persona dentro."
          : `Hay ${participantes} personas dentro.`}
      </p>

      <label className="cm-apodo">
        <span>Si prefieres, usa un apodo en vez de tu nombre</span>
        <input
          type="text"
          value={apodo}
          maxLength={30}
          placeholder="Opcional"
          onChange={(e) => setApodo(e.target.value)}
        />
        <small>Con apodo tampoco se enseña tu foto.</small>
      </label>

      <button type="button" className="cm-btn" onClick={() => onEntrar(apodo)} disabled={guardando}>
        {guardando ? "Entrando…" : "Entrar en la comunidad"}
      </button>
      <small className="cm-puerta-legal">
        Puedes salir cuando quieras y dejarás de aparecer al momento. No se
        comparte tu peso, ni tus medidas, ni nada de tu ficha.
      </small>
    </section>
  );
}

/* ─── El reto del mes ─── */

function Reto({ reto }) {
  const p = reto.progreso;
  if (!p) return null;
  const vaBien = p.proyeccion >= reto.objetivo;

  return (
    <section className={p.conseguido ? "cm-reto cm-reto-ok" : "cm-reto"}>
      <header>
        <FiTarget aria-hidden="true" />
        <div>
          <h3>{reto.titulo}</h3>
          {reto.descripcion && <p>{reto.descripcion}</p>}
        </div>
      </header>

      <div className="cm-reto-cifra">
        <strong>{p.suma.toLocaleString("es-ES")}</strong>
        <span>
          de {reto.objetivo.toLocaleString("es-ES")} {METRICA[reto.metrica]}
        </span>
      </div>

      <div className="cm-barra">
        <div style={{ width: `${p.porcentaje}%` }} />
      </div>

      <footer>
        {p.conseguido ? (
          <span className="cm-reto-hecho">
            <FiAward aria-hidden="true" /> ¡Conseguido entre {p.participantes}!
          </span>
        ) : (
          <>
            <span>
              <FiTrendingUp aria-hidden="true" />
              {vaBien
                ? `A este ritmo llegáis: ${p.proyeccion} a final de mes.`
                : `A este ritmo os quedáis en ${p.proyeccion}. Hay que apretar.`}
            </span>
            <span>{p.diasRestantes === 0 ? "Último día" : `Quedan ${p.diasRestantes} días`}</span>
          </>
        )}
      </footer>

      {reto.recompensa && !p.conseguido && (
        <p className="cm-recompensa">
          <FiAward aria-hidden="true" /> {reto.recompensa}
        </p>
      )}
    </section>
  );
}

/* ─── El muro ─── */

function ComunidadPanel() {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [ajustes, setAjustes] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/comunidad`, { headers: await auth() });
      setDatos(res.data);
      setError("");
    } catch {
      setError("No se pudo cargar la comunidad.");
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const preferencias = async (visible, apodo = datos?.apodo || "") => {
    setGuardando(true);
    try {
      await axios.put(`${API_URL}/comunidad/preferencias`, { visible, apodo }, { headers: await auth() });
      setAjustes(false);
      await cargar();
    } catch {
      setError("No se pudo guardar.");
    }
    setGuardando(false);
  };

  // Optimista: la reacción se ve al instante y se corrige si el servidor dice otra cosa
  const reaccionar = async (logroId, emoji) => {
    setDatos((d) => ({
      ...d,
      logros: d.logros.map((l) => {
        if (l._id !== logroId) return l;
        const existe = l.reacciones.find((r) => r.emoji === emoji);
        const reacciones = existe
          ? l.reacciones.map((r) =>
              r.emoji === emoji ? { ...r, mia: !r.mia, cuantos: r.cuantos + (r.mia ? -1 : 1) } : r
            )
          : [...l.reacciones, { emoji, cuantos: 1, mia: true }];
        return { ...l, reacciones: reacciones.filter((r) => r.cuantos > 0) };
      }),
    }));
    try {
      await axios.post(`${API_URL}/comunidad/${logroId}/reaccion`, { emoji }, { headers: await auth() });
    } catch {
      cargar();
    }
  };

  if (error && !datos) return <div className="app-estado app-estado-error">{error}</div>;
  if (!datos) return <div className="app-estado">Cargando…</div>;

  if (!datos.participo) {
    return <Puerta onEntrar={(apodo) => preferencias(true, apodo)} participantes={datos.participantes} guardando={guardando} />;
  }

  return (
    <div className="cm">
      <header className="cm-top">
        <div>
          <h2>El muro</h2>
          <p>
            {datos.participantes} {datos.participantes === 1 ? "persona dentro" : "personas dentro"}
            {datos.apodo && ` · sales como “${datos.apodo}”`}
          </p>
        </div>
        <div className="cm-top-acciones">
          <button type="button" onClick={cargar} aria-label="Actualizar">
            <FiRefreshCw />
          </button>
          <button type="button" onClick={() => setAjustes((v) => !v)}>
            Ajustes
          </button>
        </div>
      </header>

      {ajustes && (
        <div className="cm-ajustes">
          <p>
            Si sales, dejas de aparecer en el muro al momento y no ves el de
            los demás. Tus logros no se borran: si vuelves, siguen ahí.
          </p>
          <button type="button" className="cm-btn-salir" onClick={() => preferencias(false)} disabled={guardando}>
            <FiLock aria-hidden="true" /> Salir de la comunidad
          </button>
        </div>
      )}

      {datos.reto && <Reto reto={datos.reto} />}

      {datos.logros.length === 0 ? (
        <p className="cm-vacio">
          Todavía no hay nada en el muro. En cuanto alguien termine un entreno,
          aparecerá aquí. Si quieres estrenarlo tú, ya sabes.
        </p>
      ) : (
        <ul className="cm-muro">
          {datos.logros.map((l) => (
            <li key={l._id} className={l.mio ? "cm-logro cm-logro-mio" : "cm-logro"}>
              <div className="cm-logro-foto">
                {l.autor.conFoto ? (
                  <Avatar userId={l.autor._id} nombre={l.autor.nombre} apellido={l.autor.apellido} tamano={40} />
                ) : (
                  <span className="cm-anon">{l.autor.nombre[0]?.toUpperCase() || "·"}</span>
                )}
                <span className="cm-logro-icono" aria-hidden="true">
                  {ICONOS[l.tipo] || "💪"}
                </span>
              </div>

              <div className="cm-logro-cuerpo">
                <p>{l.texto}</p>
                <span className="cm-cuando">
                  {cuando(l.cuando)}
                  {l.mio && " · tuyo"}
                </span>

                <div className="cm-reacciones">
                  {l.reacciones.map((r) => (
                    <button
                      key={r.emoji}
                      type="button"
                      className={r.mia ? "cm-reac cm-reac-mia" : "cm-reac"}
                      onClick={() => reaccionar(l._id, r.emoji)}
                    >
                      <span aria-hidden="true">{r.emoji}</span> {r.cuantos}
                    </button>
                  ))}
                  <div className="cm-anadir-reac">
                    <button type="button" aria-label="Reaccionar">
                      <FiZap />
                    </button>
                    <div className="cm-emojis">
                      {datos.emojis.map((e) => (
                        <button key={e} type="button" onClick={() => reaccionar(l._id, e)} aria-label={`Reaccionar ${e}`}>
                          {e}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default ComunidadPanel;
